import React, { useState, useEffect } from 'react';
import {
    Settings, X, Network, Globe, Shield, Check, AlertTriangle,
    Plus, Group, ArrowLeft
} from 'lucide-react';

import type { SubnetData } from './SubnetModal';
import { showError, showSuccess } from '../../../utils/toast';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

type AddressType = 'static_ip' | 'cidr' | 'ip_range' | 'domain';

interface SubnetFormProps {
    agent: {
        client_id: string;
        id: string;
    };
    organizationId: string;
    subnet: SubnetData | null;
    isEdit: boolean;
    onSuccess: () => void;
    onBack: () => void;
}

const SubnetForm: React.FC<SubnetFormProps> = ({
    agent,
    organizationId,
    subnet,
    isEdit,
    onSuccess,
    onBack
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addressType, setAddressType] = useState<AddressType>('static_ip');
    const [formData, setFormData] = useState<SubnetData>({
        name: '',
        subnet: '',
        description: '',
        enabled: false,
        priority: 1,
        excludeIPs: [],
        ports: []
    });

    const [ipRangeStart, setIpRangeStart] = useState('');
    const [ipRangeEnd, setIpRangeEnd] = useState('');
    const [excludeMacs, setExcludeMacs] = useState<string[]>([]);
    const [newExcludeMac, setNewExcludeMac] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [newExcludeIP, setNewExcludeIP] = useState('');
    const [newPort, setNewPort] = useState('');

    const addressTypeOptions = [
        { label: 'Static IP', value: 'static_ip' },
        { label: 'CIDR', value: 'cidr' },
        { label: 'IP Range', value: 'ip_range' },
        { label: 'Domain', value: 'domain' }
    ];

    useEffect(() => {
        if (isEdit && subnet) {
            setFormData({
                ...subnet,
                excludeIPs: subnet.excludeIPs || [],
                ports: subnet.ports || []
            });
        }
    }, [isEdit, subnet]);

    // Validation helper functions
    const isValidIPv4 = (ip: string): boolean => {
        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        return ipv4Regex.test(ip);
    };

    const isValidCIDR = (cidr: string): boolean => {
        const cidrRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([8-9]|[12][0-9]|3[0-2])$/;
        return cidrRegex.test(cidr);
    };

    const isValidMac = (mac: string): boolean => {
        const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        return macRegex.test(mac);
    };

    const isValidDomain = (domain: string): boolean => {
        const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return domainRegex.test(domain);
    };

    const ipToNumber = (ip: string): number => {
        return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet), 0) >>> 0;
    };

    const isIPInCIDRRange = (ip: string, cidr: string): boolean => {
        const [baseIP, prefixStr] = cidr.split('/');
        const prefix = parseInt(prefixStr);
        const mask = ~((1 << (32 - prefix)) - 1);

        const ipNum = ipToNumber(ip);
        const baseNum = ipToNumber(baseIP);

        return (ipNum & mask) === (baseNum & mask);
    };

    const isIPInRange = (ip: string, startIP: string, endIP: string): boolean => {
        const ipNum = ipToNumber(ip);
        const startNum = ipToNumber(startIP);
        const endNum = ipToNumber(endIP);

        return ipNum >= startNum && ipNum <= endNum;
    };

    const isValidPort = (port: number): boolean => {
        return !isNaN(port) && port > 0 && port <= 65535;
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Network name is required';
        }

        // Address type specific validation
        if (addressType === 'static_ip') {
            if (!formData.subnet.trim()) {
                newErrors.subnet = 'IP address is required';
            } else if (!isValidIPv4(formData.subnet)) {
                newErrors.subnet = 'Invalid IP address format';
            }
        } else if (addressType === 'cidr') {
            if (!formData.subnet.trim()) {
                newErrors.subnet = 'CIDR notation is required';
            } else if (!isValidCIDR(formData.subnet)) {
                newErrors.subnet = 'Invalid CIDR format (e.g., 192.168.1.0/24, subnet must be 8-32)';
            }

            // Validate exclude IPs are within CIDR range
            formData.excludeIPs.forEach((ip, index) => {
                if (ip.trim() && isValidIPv4(ip)) {
                    if (!isIPInCIDRRange(ip, formData.subnet)) {
                        newErrors[`excludeIP_${index}`] = 'IP not in CIDR range';
                    }
                }
            });
        } else if (addressType === 'ip_range') {
            if (!ipRangeStart.trim() || !ipRangeEnd.trim()) {
                newErrors.subnet = 'Both start and end IP addresses are required';
            } else if (!isValidIPv4(ipRangeStart)) {
                newErrors.ipRangeStart = 'Invalid start IP address';
            } else if (!isValidIPv4(ipRangeEnd)) {
                newErrors.ipRangeEnd = 'Invalid end IP address';
            } else if (ipToNumber(ipRangeStart) > ipToNumber(ipRangeEnd)) {
                newErrors.ipRangeEnd = 'End IP must be greater than start IP';
            }

            // Validate exclude IPs are within range
            formData.excludeIPs.forEach((ip, index) => {
                if (ip.trim() && isValidIPv4(ip)) {
                    if (!isIPInRange(ip, ipRangeStart, ipRangeEnd)) {
                        newErrors[`excludeIP_${index}`] = 'IP not in specified range';
                    }
                }
            });
        } else if (addressType === 'domain') {
            if (!formData.subnet.trim()) {
                newErrors.subnet = 'Domain is required';
            } else if (!isValidDomain(formData.subnet)) {
                newErrors.subnet = 'Invalid domain format';
            }
        }

        // Validate exclude IPs format
        formData.excludeIPs.forEach((ip, index) => {
            if (ip.trim() && !isValidIPv4(ip.trim())) {
                newErrors[`excludeIP_${index}`] = 'Invalid IP format';
            }
        });

        // Validate exclude MACs format
        excludeMacs.forEach((mac, index) => {
            if (mac.trim() && !isValidMac(mac.trim())) {
                newErrors[`excludeMAC_${index}`] = 'Invalid MAC format (e.g., AA:BB:CC:DD:EE:FF)';
            }
        });

        // Validate ports
        formData.ports.forEach((port, index) => {
            if (!isValidPort(port)) {
                newErrors[`port_${index}`] = 'Invalid port number';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            let subnetValue = formData.subnet;
            if (addressType === 'ip_range') {
                subnetValue = `${ipRangeStart}-${ipRangeEnd}`;
            }

            const params = {
                client_id: agent.client_id,
                agent_id: agent.id,
                organization_id: organizationId,
                ...formData,
                subnet: subnetValue,
                address_type: addressType,
                exclude_ips: formData.excludeIPs.filter(ip => ip.trim()),
                exclude_macs: excludeMacs.filter(mac => mac.trim()),
                ports: formData.ports
            };

            let subnetId: string | undefined;
            if (isEdit && subnet?.id) {
                subnetId = subnet.id;
            }

            const endpoint = isEdit ? `/network_configuration/${subnetId}` : '/network_configuration';
            const method = isEdit ? 'put' : 'post';

            const response = await baseapi[method](endpoint, params);

            if (response.data.status) {
                showSuccess(isEdit ? 'Subnet updated successfully' : 'Subnet configured successfully');
                onSuccess();
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Submit failed:', error);
            showError('Failed to save subnet configuration');
        } finally {
            setIsSubmitting(false);
        }
    };

    const addExcludeIP = () => {
        if (newExcludeIP.trim()) {
            if (!isValidIPv4(newExcludeIP.trim())) {
                setErrors(prev => ({ ...prev, newExcludeIP: 'Invalid IP format' }));
                return;
            }

            // Validate IP is in range for CIDR or IP Range
            if (addressType === 'cidr' && formData.subnet) {
                if (!isIPInCIDRRange(newExcludeIP.trim(), formData.subnet)) {
                    setErrors(prev => ({ ...prev, newExcludeIP: 'IP not in CIDR range' }));
                    return;
                }
            } else if (addressType === 'ip_range' && ipRangeStart && ipRangeEnd) {
                if (!isIPInRange(newExcludeIP.trim(), ipRangeStart, ipRangeEnd)) {
                    setErrors(prev => ({ ...prev, newExcludeIP: 'IP not in specified range' }));
                    return;
                }
            }

            setFormData(prev => ({
                ...prev,
                excludeIPs: [...prev.excludeIPs, newExcludeIP.trim()]
            }));
            setNewExcludeIP('');
            setErrors(prev => {
                const { newExcludeIP, ...rest } = prev;
                return rest;
            });
        }
    };

    const removeExcludeIP = (index: number) => {
        setFormData(prev => ({
            ...prev,
            excludeIPs: prev.excludeIPs.filter((_, i) => i !== index)
        }));
    };

    const addExcludeMac = () => {
        if (newExcludeMac.trim()) {
            if (!isValidMac(newExcludeMac.trim())) {
                setErrors(prev => ({ ...prev, newExcludeMac: 'Invalid MAC format (e.g., AA:BB:CC:DD:EE:FF)' }));
                return;
            }

            setExcludeMacs(prev => [...prev, newExcludeMac.trim()]);
            setNewExcludeMac('');
            setErrors(prev => {
                const { newExcludeMac, ...rest } = prev;
                return rest;
            });
        }
    };

    const removeExcludeMac = (index: number) => {
        setExcludeMacs(prev => prev.filter((_, i) => i !== index));
    };

    const addPort = () => {
        const portNumber = parseInt(newPort.trim());
        if (newPort.trim() && isValidPort(portNumber)) {
            if (!formData.ports.includes(portNumber)) {
                setFormData(prev => ({
                    ...prev,
                    ports: [...prev.ports, portNumber]
                }));
            }
            setNewPort('');
        }
    };

    const removePort = (index: number) => {
        setFormData(prev => ({
            ...prev,
            ports: prev.ports.filter((_, i) => i !== index)
        }));
    };

    const handleExcludeIPKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addExcludeIP();
        }
    };

    const handleExcludeMacKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addExcludeMac();
        }
    };

    const handlePortKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addPort();
        }
    };

    const handlePortInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setNewPort(value);
        }
    };

    const isPortInputValid = () => {
        if (!newPort.trim()) return false;
        const portNumber = parseInt(newPort.trim());
        return isValidPort(portNumber) && !formData.ports.includes(portNumber);
    };

    const renderSubnetInput = () => {
        switch (addressType) {
            case 'static_ip':
                return (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Globe className="w-4 h-4 mr-2 text-blue-400" />
                            IP Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.subnet}
                            onChange={(e) => setFormData(prev => ({ ...prev, subnet: e.target.value }))}
                            className={`w-full bg-gray-800/50 border ${errors.subnet ? 'border-red-400' : 'border-gray-700'
                                } rounded-lg px-4 py-3 text-white placeholder-gray-400 font-mono
                                focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                transition-all duration-200`}
                            placeholder="e.g., 192.168.1.1"
                        />
                        {errors.subnet && (
                            <p className="mt-1 text-sm text-red-400 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {errors.subnet}
                            </p>
                        )}
                    </div>
                );

            case 'cidr':
                return (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Globe className="w-4 h-4 mr-2 text-blue-400" />
                            CIDR Notation <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.subnet}
                            onChange={(e) => setFormData(prev => ({ ...prev, subnet: e.target.value }))}
                            className={`w-full bg-gray-800/50 border ${errors.subnet ? 'border-red-400' : 'border-gray-700'
                                } rounded-lg px-4 py-3 text-white placeholder-gray-400 font-mono
                                focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                transition-all duration-200`}
                            placeholder="e.g., 192.168.1.0/24"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            Subnet must be between /8 and /32
                        </p>
                        {errors.subnet && (
                            <p className="mt-1 text-sm text-red-400 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {errors.subnet}
                            </p>
                        )}
                    </div>
                );

            case 'ip_range':
                return (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Globe className="w-4 h-4 mr-2 text-blue-400" />
                            IP Range <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <input
                                    type="text"
                                    value={ipRangeStart}
                                    onChange={(e) => setIpRangeStart(e.target.value)}
                                    className={`w-full bg-gray-800/50 border ${errors.ipRangeStart ? 'border-red-400' : 'border-gray-700'
                                        } rounded-lg px-4 py-3 text-white placeholder-gray-400 font-mono
                                        focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                        transition-all duration-200`}
                                    placeholder="Start IP"
                                />
                                {errors.ipRangeStart && (
                                    <p className="mt-1 text-xs text-red-400">{errors.ipRangeStart}</p>
                                )}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={ipRangeEnd}
                                    onChange={(e) => setIpRangeEnd(e.target.value)}
                                    className={`w-full bg-gray-800/50 border ${errors.ipRangeEnd ? 'border-red-400' : 'border-gray-700'
                                        } rounded-lg px-4 py-3 text-white placeholder-gray-400 font-mono
                                        focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                        transition-all duration-200`}
                                    placeholder="End IP"
                                />
                                {errors.ipRangeEnd && (
                                    <p className="mt-1 text-xs text-red-400">{errors.ipRangeEnd}</p>
                                )}
                            </div>
                        </div>
                        {errors.subnet && (
                            <p className="mt-1 text-sm text-red-400 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {errors.subnet}
                            </p>
                        )}
                    </div>
                );

            case 'domain':
                return (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Globe className="w-4 h-4 mr-2 text-blue-400" />
                            Domain <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.subnet}
                            onChange={(e) => setFormData(prev => ({ ...prev, subnet: e.target.value }))}
                            className={`w-full bg-gray-800/50 border ${errors.subnet ? 'border-red-400' : 'border-gray-700'
                                } rounded-lg px-4 py-3 text-white placeholder-gray-400 font-mono
                                focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                transition-all duration-200`}
                            placeholder="e.g., example.com"
                        />
                        {errors.subnet && (
                            <p className="mt-1 text-sm text-red-400 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {errors.subnet}
                            </p>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={onBack}
                        className="mr-3 text-gray-400 hover:text-white transition-colors p-1 
                            rounded-full hover:bg-gray-800"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="mr-3 text-orange-400">
                        <Settings
                            className="h-6 w-6"
                            style={isSubmitting ? { animation: 'spin 3s linear infinite' } : {}}
                        />
                    </div>
                    <h2 className="text-2xl font-bold text-white">
                        {isEdit ? 'Edit Subnet' : 'Add Subnet'}
                    </h2>
                </div>
            </div>

            <div className="space-y-5">
                {/* Network Name */}
                <div>
                    <label className="flex items-center text-sm font-medium text-white mb-2">
                        <Network className="w-4 h-4 mr-2 text-orange-400" />
                        Network Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className={`w-full bg-gray-800/50 border ${errors.name ? 'border-red-400' : 'border-gray-700'
                            } rounded-lg px-4 py-3 text-white placeholder-gray-400 
                            focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                            transition-all duration-200`}
                        placeholder="Enter network name"
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-400 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Address Type and Status Row */}
                <div className="">
                    <div className="">
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Network className="w-4 h-4 mr-2 text-cyan-400" />
                            Address Type <span className="text-red-500">*</span>
                        </label>
                        <CustomDropdown
                            data={addressTypeOptions}
                            placeholder="Select address type"
                            selectedValue={addressType}
                            onSelect={(value) => {
                                setAddressType(value as AddressType);
                                setFormData(prev => ({ ...prev, subnet: '' }));
                                setIpRangeStart('');
                                setIpRangeEnd('');
                                setErrors({});
                            }}
                            width="w-full"
                            buttonHeight={12}
                        />
                    </div>


                </div>

                {/* Dynamic Subnet Input */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3">
                        {renderSubnetInput()}
                    </div>
                    <div className="md:col-span-1">
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Settings className="w-4 h-4 text-cyan-400 mr-2" />
                            Exclude this IP
                        </label>
                        <div className="flex items-center h-12">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full 
                                    transition-colors duration-200 focus:outline-none ${formData.enabled ? 'bg-orange-600' : 'bg-gray-600'
                                    }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white 
                                        transition-transform duration-200 ${formData.enabled ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                />
                            </button>
                            <span className={`ml-3 text-sm font-medium ${formData.enabled ? 'text-green-400' : 'text-gray-400'
                                }`}>
                                {formData.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="flex items-center text-sm font-medium text-white mb-2">
                        <Shield className="w-4 h-4 text-green-400 mr-2" />
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 
                            text-white placeholder:text-gray-400
                            focus:outline-none focus:border-orange-400 focus:ring-1 
                            focus:ring-orange-400 transition-all duration-200 resize-none"
                        placeholder="Enter network description..."
                    />
                </div>

                {/* Exclude IPs - Only for CIDR and IP Range */}
                {(addressType === 'cidr' || addressType === 'ip_range') && (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Shield className="w-4 h-4 text-yellow-400 mr-2" />
                            Exclude IPs
                        </label>
                        <div className="flex items-center gap-2 mb-3">
                            <input
                                type="text"
                                value={newExcludeIP}
                                onChange={(e) => setNewExcludeIP(e.target.value)}
                                onKeyPress={handleExcludeIPKeyPress}
                                className={`flex-1 bg-gray-800/50 border ${errors.newExcludeIP ? 'border-red-400' : 'border-gray-700'
                                    } rounded-lg px-3 py-2 
                                    text-white placeholder-gray-400 font-mono text-sm
                                    focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                    transition-all duration-200`}
                                placeholder="Enter IP to exclude"
                            />
                            <button
                                type="button"
                                onClick={addExcludeIP}
                                disabled={!newExcludeIP.trim()}
                                className="flex items-center gap-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 
                                    text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                        {errors.newExcludeIP && (
                            <p className="mb-2 text-xs text-red-400">{errors.newExcludeIP}</p>
                        )}
                        {formData.excludeIPs.length > 0 && (
                            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 bg-gray-800/20 rounded-lg border border-gray-700/50">
                                {formData.excludeIPs.map((ip, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-1 bg-yellow-600/20 border border-yellow-600/30 
                                            rounded-lg px-2 py-1 text-sm"
                                    >
                                        <span className="text-yellow-200 font-mono">{ip}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeExcludeIP(index)}
                                            className="text-red-400 hover:text-red-300 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Exclude MACs - Only for CIDR and IP Range */}
                {(addressType === 'cidr' || addressType === 'ip_range') && (
                    <div>
                        <label className="flex items-center text-sm font-medium text-white mb-2">
                            <Shield className="w-4 h-4 text-purple-400 mr-2" />
                            Exclude MAC Addresses
                        </label>
                        <div className="flex items-center gap-2 mb-3">
                            <input
                                type="text"
                                value={newExcludeMac}
                                onChange={(e) => setNewExcludeMac(e.target.value)}
                                onKeyPress={handleExcludeMacKeyPress}
                                className={`flex-1 bg-gray-800/50 border ${errors.newExcludeMac ? 'border-red-400' : 'border-gray-700'
                                    } rounded-lg px-3 py-2 
                                    text-white placeholder-gray-400 font-mono text-sm
                                    focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                    transition-all duration-200`}
                                placeholder="e.g., AA:BB:CC:DD:EE:FF"
                            />
                            <button
                                type="button"
                                onClick={addExcludeMac}
                                disabled={!newExcludeMac.trim()}
                                className="flex items-center gap-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 
                                    text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                        {errors.newExcludeMac && (
                            <p className="mb-2 text-xs text-red-400">{errors.newExcludeMac}</p>
                        )}
                        {excludeMacs.length > 0 && (
                            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 bg-gray-800/20 rounded-lg border border-gray-700/50">
                                {excludeMacs.map((mac, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-1 bg-purple-600/20 border border-purple-600/30 
                                            rounded-lg px-2 py-1 text-sm"
                                    >
                                        <span className="text-purple-200 font-mono">{mac}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeExcludeMac(index)}
                                            className="text-red-400 hover:text-red-300 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Ports */}
                <div>
                    <label className="flex items-center text-sm font-medium text-white mb-2">
                        <Group className="w-4 h-4 text-indigo-400 mr-2" />
                        Ports (Optional)
                    </label>
                    <div className="flex items-center gap-2 mb-3">
                        <input
                            type="text"
                            value={newPort}
                            onChange={handlePortInputChange}
                            onKeyPress={handlePortKeyPress}
                            className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 
                                text-white placeholder-gray-400 font-mono text-sm
                                focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                transition-all duration-200"
                            placeholder="Enter port number (1-65535)"
                        />
                        <button
                            type="button"
                            onClick={addPort}
                            disabled={!isPortInputValid()}
                            className="flex items-center gap-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 
                                text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="w-4 h-4" />
                            Add
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">
                        Enter port numbers (1-65535). Duplicates will be ignored.
                    </p>
                    {formData.ports.length > 0 && (
                        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 bg-gray-800/20 rounded-lg border border-gray-700/50">
                            {formData.ports.map((port, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-1 bg-indigo-600/20 border border-indigo-600/30 
                                        rounded-lg px-2 py-1 text-sm"
                                >
                                    <span className="text-indigo-200 font-mono">{port}</span>
                                    <button
                                        type="button"
                                        onClick={() => removePort(index)}
                                        className="text-red-400 hover:text-red-300 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-600 text-gray-300 
                        rounded-lg hover:bg-gray-800 hover:border-gray-500 transition-all duration-200 
                        disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <X className="w-4 h-4" />
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-orange-600 to-orange-700 
                        text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 
                        hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-200 
                        disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] justify-center"
                >
                    {isSubmitting ? (
                        <>
                            <Settings
                                className="w-4 h-4"
                                style={{ animation: 'spin 3s linear infinite' }}
                            />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Check className="w-4 h-4" />
                            {isEdit ? 'Update' : 'Submit'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default SubnetForm;
